import { Button, Flex, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBars from '../custom-components/LoadingBars';
import NA from '../custom-components/NA';
import NothingHere from '../custom-components/NothingHere';
import { TimestampDisplay } from '../custom-components/TimestampDisplay';
import UniversalTag from '../custom-components/UniversalTag';
import { GlobalContext } from '../global-context';
import { useMemberEmails } from '../hooks/useMemberEmails';
import { allPages, enumLabel, toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import './MatchRun.scss';

export function MatchRuns() {
  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();
  General.setTitle(document, 'HubSpot Company Enrichment', globalContext);

  const [runs, setRuns] = useState<Edulead.EduleadV4Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const memberEmails = useMemberEmails();
  const total = runs.length;
  const loadRuns = useCallback(async () => {
    try {
      setIsLoading(true);
      setRuns(await allPages(eduleadV4.matches.list({ page_size: 100 })));
    } catch (error) {
      toastV4Error(error, 'Failed to load match runs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleCreateClick = () => navigate('/tools/hubspot-companies/new');
  const handleRowClick = (runId: string) => navigate(`/tools/hubspot-companies/${runId}`);

  const matchedShare = (run: Edulead.EduleadV4Match): string | null => {
    if (run.companies_matched == null || !run.companies_total) return null;
    return `${Math.round((run.companies_matched / run.companies_total) * 100)}%`;
  };

  return (
    <div className="content content-1000">
      <div>
        <div className="match-run__header">
          <h3 className="match-run__title">
            HubSpot Company Enrichment{total > 0 ? ` (${total})` : ''}
          </h3>
          <Flex gap={5}>
            <Button size="small" className="tiny" onClick={loadRuns}>Refresh</Button>
            <Button size="small" variation="primary" className="tiny" onClick={handleCreateClick}>
              New run
            </Button>
          </Flex>
        </div>

        {isLoading ? (
          <LoadingBars numberOfRows={10} />
        ) : runs.length === 0 ? (
          <NothingHere text="No runs found" />
        ) : (
          <Table size='small' highlightOnHover={true}>
            <TableHead>
              <TableRow>
                <TableCell as="th">Created</TableCell>
                <TableCell as="th">Created by</TableCell>
                <TableCell as="th">Strategy</TableCell>
                <TableCell as="th">Status</TableCell>
                <TableCell as="th">HubSpot companies found</TableCell>
                <TableCell as="th">Matched to institutions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run: Edulead.EduleadV4Match) => (
                <TableRow key={run.id} onClick={() => handleRowClick(run.id ?? '')}>
                  <TableCell><TimestampDisplay timestamp={run.create_time} singleFormat /></TableCell>
                  <TableCell>{memberEmails.get(run.creator_id ?? '') || <NA />}</TableCell>
                  <TableCell><UniversalTag category={enumLabel(run.strategy, 'MATCH_STRATEGY_')} /></TableCell>
                  <TableCell><UniversalTag category={enumLabel(run.state, 'MATCH_STATE_')} /></TableCell>
                  <TableCell>
                    {run.companies_total != null ? run.companies_total.toLocaleString() : <NA />}
                  </TableCell>
                  <TableCell>
                    {run.companies_matched != null ? (
                      <>
                        {run.companies_matched.toLocaleString()}
                        {matchedShare(run) && ` (${matchedShare(run)})`}
                      </>
                    ) : <NA />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

    </div>
  );
}
